// Import styles
import '../css/style.css'

// Import core modules synchronously for critical functionality
import { initNavigation } from './navigation.js';
import { adaptPerformance, registerServiceWorker } from './utils.js';

// Main JavaScript functionality - optimized for performance
document.addEventListener('DOMContentLoaded', async function() {
    // Initialize critical functionality immediately
    initNavigation();
    adaptPerformance();
    
    // Register service worker for PWA
    registerServiceWorker();
    
    // Basic hero animation (critical path)
    if (window.gsap) {
        const tl = gsap.timeline();
        
        tl.from('.hero-greeting', {
            duration: 0.8,
            y: 30,
            opacity: 0,
            ease: 'power2.out'
        })
        .from('.hero-name', {
            duration: 1.2,
            scale: 0.8,
            opacity: 0,
            ease: 'elastic.out(1, 0.5)'
        }, '-=0.4')
        .from('.hero-subtitle', {
            duration: 0.8,
            y: 20,
            opacity: 0,
            ease: 'power2.out'
        }, '-=0.6')
        .from('.hero-description', {
            duration: 0.8,
            y: 20,
            opacity: 0,
            ease: 'power2.out'
        }, '-=0.4')
        .from('.hero-buttons .btn', {
            duration: 0.6,
            scale: 0.8,
            opacity: 0,
            stagger: 0.1,
            ease: 'back.out(1.7)'
        }, '-=0.2')
        .from('.hero-scroll', {
            duration: 0.8,
            y: 30,
            opacity: 0,
            ease: 'power2.out'
        }, '-=0.3');
    }
    
    // Lazy load animations module when user scrolls or after delay
    let animationsLoaded = false;
    const loadAnimations = async () => {
        if (animationsLoaded) return;
        animationsLoaded = true;
        
        try {
            const {
                initScrollAnimations,
                initTiltEffect,
                initTypingEffect,
                initMagneticButtons,
                initScrollProgress,
                initParallaxEffect
            } = await import('./animations.js');

            // Initialize animation features
            initTiltEffect();
            initTypingEffect();
            initMagneticButtons();
            initScrollProgress();
            initParallaxEffect();
            initScrollAnimations();
        } catch (error) {
            // Graceful fallback if animations fail to load
        }
    };
    
    // Lazy load effects when user interacts or after delay
    let effectsLoaded = false;
    const loadEffects = async () => {
        if (effectsLoaded) return;
        effectsLoaded = true;
        
        try {
            const {
                initCursorEffects,
                initParticleSystem,
                initKonamiCode
            } = await import('./effects.js');

            // Initialize effect features (only if performance allows)
            if (!document.body.classList.contains('reduced-animations')) {
                initCursorEffects();
                initParticleSystem();
            }
            initKonamiCode();
        } catch (error) {
            // Graceful fallback if effects fail to load
        }
    };
    
    // Load animations after initial render or on scroll
    const loadAnimationsOnInteraction = () => {
        loadAnimations();
        // Remove event listeners after first load
        window.removeEventListener('scroll', loadAnimationsOnInteraction);
        window.removeEventListener('touchstart', loadAnimationsOnInteraction);
        window.removeEventListener('mousemove', loadAnimationsOnInteraction);
    };
    
    // Schedule lazy loading with optimized timing
    setTimeout(loadAnimations, 800); // Load after 800ms
    setTimeout(loadEffects, 1200); // Load effects after 1.2 seconds
    
    // Also load on user interaction
    window.addEventListener('scroll', loadAnimationsOnInteraction, { passive: true, once: true });
    window.addEventListener('touchstart', loadAnimationsOnInteraction, { passive: true, once: true });
    window.addEventListener('mousemove', loadAnimationsOnInteraction, { passive: true, once: true });

    // --- Anchor / deep-link landing fix --------------------------------------
    // Opening the page at a hash (Projects from /blog -> /#projects) makes the browser
    // jump before Three.js, the skills canvas and images load, so the layout shifts by
    // thousands of px and it lands on the wrong section. Track the real target every
    // frame - instant, with CSS scroll-behavior forced off so 'smooth' can't animate
    // or cancel the jump - until the layout is stable. Abort the moment the user scrolls.
    const navOffset = () => {
        const nav = document.querySelector('.desktop-navbar');
        return (nav ? nav.offsetHeight : 0) + 12;
    };
    const hashTarget = () => {
        const h = location.hash;
        return h && h.length > 1 ? document.getElementById(decodeURIComponent(h.slice(1))) : null;
    };
    const targetY = (el) => Math.max(0, el.getBoundingClientRect().top + window.scrollY - navOffset());

    if (hashTarget()) {
        const rootStyle = document.documentElement.style;
        const prevBehavior = rootStyle.scrollBehavior;
        rootStyle.scrollBehavior = 'auto'; // keep our jumps instant during correction
        const restore = () => { rootStyle.scrollBehavior = prevBehavior; };

        let userTookOver = false;
        ['wheel', 'touchstart', 'keydown'].forEach(ev =>
            window.addEventListener(ev, () => { userTookOver = true; restore(); }, { passive: true, once: true }));

        loadAnimations();

        const start = performance.now();
        let lastY = -1, stableSince = 0;
        const loop = () => {
            if (userTookOver) return;
            const el = hashTarget();
            const y = el ? targetY(el) : 0;
            window.scrollTo(0, y);
            const now = performance.now();
            if (Math.abs(y - lastY) < 2) { if (!stableSince) stableSince = now; }
            else { stableSince = 0; lastY = y; }
            const settled = !!stableSince && now - stableSince > 700;
            if (!settled && now - start < 4500) requestAnimationFrame(loop);
            else {
                // Fire the scroll-reveal for the section we jumped to (its ScrollTrigger
                // otherwise stays un-played on a jump, leaving the title/cards blank).
                if (window.ScrollTrigger) window.ScrollTrigger.refresh();
                restore();
            }
        };
        requestAnimationFrame(loop);
        // If assets finish loading after we settle, refresh once more so the landed
        // section reveals (never a blank section).
        window.addEventListener('load', () => {
            if (!userTookOver && window.ScrollTrigger) window.ScrollTrigger.refresh();
        }, { once: true });
    }

    // Smooth same-page anchor scrolling that clears the fixed header.
    document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href^="#"], a[href^="/#"]');
        if (!a) return;
        const raw = a.getAttribute('href') || '';
        const hash = raw.charAt(0) === '/' ? raw.slice(1) : raw;
        if (hash.length < 2) return;
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (!el) return;
        e.preventDefault();
        history.pushState(null, '', hash);
        window.scrollTo({ top: targetY(el), behavior: 'smooth' });
    });
});